import { useState } from "react";
import { Text, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormSelect, FormSwitch, FormTextarea, CollapsibleSection, ItemCard, ItemHeader, AddButton, RepeatableText, Grid2, Divider, SubsectionTitle } from "@/components/jobOffer/ui";
import { sumMaxScore } from "@/scoringUi";
import { createEmptyQualifyingQuestion } from "@/models/campaign";
import CampaignInheritedCriteriaSection from "@/components/campaign/CampaignInheritedCriteriaSection";
import ScoringModelSummary from "@/components/jobOffer/ScoringModelSummary";

const ANSWER_TYPE_OPTIONS = [
    { value: "Text", label: "Tekst" },
    { value: "Number", label: "Liczba" },
    { value: "Date", label: "Data" },
    { value: "YesNo", label: "Tak / Nie" },
    { value: "SingleChoice", label: "Jedna odpowiedź" },
    { value: "MultiChoice", label: "Wiele odpowiedzi" },
    { value: "Email", label: "E-mail" },
    { value: "Phone", label: "Telefon" },
    { value: "Address", label: "Adres" },
];

const CHOICE_ANSWER_TYPES = ["SingleChoice", "MultiChoice"];

const DISQUALIFICATION_ACTION_OPTIONS = [
    { value: "EndProcess", label: "Zakończ proces (brak dalszego kontaktu)" },
    { value: "ProposeAlternative", label: "Zaproponuj alternatywną usługę" },
    { value: "LeaveForLater", label: "Zostaw kontakt do późniejszej obsługi" },
    { value: "HandoffToOtherCampaign", label: "Przekaż do innej kampanii" },
];

const Step7KnowledgeQualifying = ({ values, setValues, campaignId, scenarioId, forceOpen }) => {
    const [scenarioModel, setScenarioModel] = useState(null);
    const scenarioMaxScore = scenarioModel?.criteria
        ? sumMaxScore(scenarioModel.criteria.map(c => ({ scoring: c.scoring })))
        : 0;

    const set = (key, val) => setValues({ ...values, [key]: val });
    const setCompanyInfo = (key, val) => setValues({ ...values, companyInfo: { ...values.companyInfo, [key]: val } });

    const questions = values.qualifyingQuestions ?? [];
    const setQuestions = (next) => set("qualifyingQuestions", next);
    const updateQuestion = (i, patch) => setQuestions(questions.map((q, idx) => idx === i ? { ...q, ...patch } : q));
    const removeQuestion = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
    const addQuestion = () => setQuestions([...questions, createEmptyQualifyingQuestion()]);

    return (
        <VStack w="full" align="stretch" gap={4}>
            <CollapsibleSection title="Informacje o firmie" subtitle="Baza wiedzy, z której korzysta Agent — opcjonalne, ale poprawia jakość odpowiedzi" defaultOpen forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    <Field label="Nazwa firmy">
                        <FormInput value={values.companyInfo?.companyName ?? ""} onChange={(e) => setCompanyInfo("companyName", e.target.value)} />
                    </Field>
                    <Field label="Krótki opis firmy">
                        <FormTextarea value={values.companyInfo?.shortDescription ?? ""} onChange={(e) => setCompanyInfo("shortDescription", e.target.value)} rows={2} />
                    </Field>

                    <CollapsibleSection title="Więcej o firmie" subtitle="Marka, WWW, doświadczenie, przewagi, referencje — opcjonalne">
                        <VStack align="stretch" gap={4}>
                            <Grid2>
                                <Field label="Marka">
                                    <FormInput value={values.companyInfo?.brand ?? ""} onChange={(e) => setCompanyInfo("brand", e.target.value)} />
                                </Field>
                                <Field label="Strona WWW">
                                    <FormInput value={values.companyInfo?.website ?? ""} onChange={(e) => setCompanyInfo("website", e.target.value)} />
                                </Field>
                            </Grid2>
                            <Field label="Obszar działania">
                                <FormInput value={values.companyInfo?.serviceArea ?? ""} onChange={(e) => setCompanyInfo("serviceArea", e.target.value)} />
                            </Field>
                            <Field label="Czym firma się zajmuje">
                                <FormTextarea value={values.companyInfo?.whatWeDo ?? ""} onChange={(e) => setCompanyInfo("whatWeDo", e.target.value)} rows={2} />
                            </Field>
                            <Field label="Dlaczego klient powinien wybrać firmę">
                                <FormTextarea value={values.companyInfo?.whyChooseUs ?? ""} onChange={(e) => setCompanyInfo("whyChooseUs", e.target.value)} rows={2} />
                            </Field>
                            <Field label="Doświadczenie">
                                <FormTextarea value={values.companyInfo?.experience ?? ""} onChange={(e) => setCompanyInfo("experience", e.target.value)} rows={2} />
                            </Field>
                            <Divider />
                            <SubsectionTitle>Przewagi, referencje, certyfikaty</SubsectionTitle>
                            <Grid2>
                                <RepeatableText label="Najważniejsze przewagi" values={values.companyInfo?.keyAdvantages ?? []} onChange={(v) => setCompanyInfo("keyAdvantages", v)} placeholder="Przewaga" />
                                <RepeatableText label="Referencje" values={values.companyInfo?.references ?? []} onChange={(v) => setCompanyInfo("references", v)} placeholder="Referencja" />
                                <RepeatableText label="Certyfikaty" values={values.companyInfo?.certificates ?? []} onChange={(v) => setCompanyInfo("certificates", v)} placeholder="Certyfikat" />
                                <RepeatableText label="Nagrody" values={values.companyInfo?.awards ?? []} onChange={(v) => setCompanyInfo("awards", v)} placeholder="Nagroda" />
                                <RepeatableText label="Partnerzy" values={values.companyInfo?.partners ?? []} onChange={(v) => setCompanyInfo("partners", v)} placeholder="Partner" />
                                <RepeatableText label="Lokalizacje" values={values.companyInfo?.locations ?? []} onChange={(v) => setCompanyInfo("locations", v)} placeholder="Lokalizacja" />
                            </Grid2>
                            <Field label="Godziny działania">
                                <FormInput value={values.companyInfo?.operatingHours ?? ""} onChange={(e) => setCompanyInfo("operatingHours", e.target.value)} />
                            </Field>
                        </VStack>
                    </CollapsibleSection>
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Pytania i kryteria kwalifikacji" subtitle="Pytania, które Agent ma zebrać, i kiedy lead jest zakwalifikowany lub odrzucony" forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    {questions.map((q, i) => {
                        const priorQuestions = questions.slice(0, i);
                        const conditionOptions = [
                            { value: "", label: "Brak warunku" },
                            ...priorQuestions.map((p, idx) => ({ value: p.id, label: p.question || `Pytanie ${idx + 1}` })),
                        ];
                        const isChoiceType = CHOICE_ANSWER_TYPES.includes(q.answerType);

                        return (
                            <ItemCard key={q.id ?? i}>
                                <ItemHeader title={q.question || "Nowe pytanie"} index={i} onRemove={() => removeQuestion(i)} />
                                <VStack align="stretch" gap={3}>
                                    <Field label="Treść pytania" required>
                                        <FormInput value={q.question} onChange={(e) => updateQuestion(i, { question: e.target.value })} />
                                    </Field>
                                    <Field label="Typ odpowiedzi">
                                        <FormSelect value={q.answerType} onChange={(v) => updateQuestion(i, { answerType: v })} options={ANSWER_TYPE_OPTIONS} placeholder="Wybierz typ" />
                                    </Field>
                                    {isChoiceType && (
                                        <RepeatableText label="Możliwe odpowiedzi" values={q.options ?? []} onChange={(v) => updateQuestion(i, { options: v })} placeholder="Odpowiedź" />
                                    )}
                                    {priorQuestions.length > 0 && (
                                        <Field label="Warunek zadania pytania" hint="Pokaż tylko jeśli odpowiedź na wcześniejsze pytanie =">
                                            <FormSelect
                                                value={q.conditionQuestionId ?? ""}
                                                onChange={(v) => updateQuestion(i, v ? { conditionQuestionId: v } : { conditionQuestionId: null, conditionValue: "" })}
                                                options={conditionOptions}
                                                placeholder="Brak warunku"
                                            />
                                        </Field>
                                    )}
                                    {q.conditionQuestionId && (
                                        <Field label="Oczekiwana wartość odpowiedzi" hint='np. "TAK"'>
                                            <FormInput value={q.conditionValue ?? ""} onChange={(e) => updateQuestion(i, { conditionValue: e.target.value })} />
                                        </Field>
                                    )}
                                    <Grid2>
                                        <FormSwitch label="Wymagane" checked={!!q.isRequired} onChange={(v) => updateQuestion(i, { isRequired: v })} />
                                        <FormSwitch label="Krytyczne (eliminacyjne)" checked={!!q.isCritical} onChange={(v) => updateQuestion(i, { isCritical: v })} />
                                    </Grid2>
                                </VStack>
                            </ItemCard>
                        );
                    })}
                    <AddButton label="Dodaj pytanie kwalifikujące" onClick={addQuestion} />

                    <RepeatableText
                        label="Kryteria kwalifikacji (QUALIFIED)"
                        values={values.qualificationCriteria ?? []}
                        onChange={(v) => set("qualificationCriteria", v)}
                        placeholder='np. "budżet > 20 000 PLN"'
                    />

                    <Field label="Akcja przy dyskwalifikacji (DISQUALIFIED)">
                        <FormSelect
                            value={values.disqualificationAction ?? ""}
                            onChange={(v) => set("disqualificationAction", v || null)}
                            options={DISQUALIFICATION_ACTION_OPTIONS}
                            placeholder="Wybierz akcję"
                        />
                    </Field>
                    {values.disqualificationAction && (
                        <Field label="Szczegóły" hint="np. nazwa alternatywnej usługi lub kampanii docelowej">
                            <FormTextarea value={values.disqualificationActionDetail ?? ""} onChange={(e) => set("disqualificationActionDetail", e.target.value)} rows={2} />
                        </Field>
                    )}
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Kryteria ze scenariusza" subtitle="Kryteria zdefiniowane na poziomie scenariusza, z możliwością nadpisania dla tej kampanii" forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    <ScoringModelSummary localCriteria={[]} scenarioMaxScore={scenarioMaxScore} />
                    <CampaignInheritedCriteriaSection
                        campaignId={campaignId}
                        scenarioId={scenarioId}
                        scoringOverrides={values.scoringOverrides ?? []}
                        onOverridesChange={(next) => setValues({ ...values, scoringOverrides: next })}
                        onModelLoaded={setScenarioModel}
                    />
                    {!scenarioModel?.criteria?.length && (
                        <Text color="text.grey" fontSize="sm">Ten scenariusz nie ma jeszcze zdefiniowanych kryteriów kwalifikujących.</Text>
                    )}
                </VStack>
            </CollapsibleSection>
        </VStack>
    );
};

export default Step7KnowledgeQualifying;
